import React, { useState } from 'react';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import styled from 'styled-components';
import { enqueueSnackbar } from 'notistack';
import Switch from '../../Shared/Switch';
import Tooltip from '../../Shared/StyledTooltip';
import { Pharmacy } from '../../../types/pharmacy';
import { useUpdateInactiveOrdering, getMissingRequirements } from './hooks/useInactiveOrdering';

// Mirrors REQUIREMENTS_TO_ENABLE in the api use case. The branch GET normalises address lines to
// camelCase, but snake_case is accepted too so this holds against either shape.
const REQUIREMENTS: { field: string; label: string; isPresent: (pharmacy: any) => boolean }[] = [
  { field: 'NACSCode', label: 'NACS code', isPresent: p => Boolean(p.NACSCode) },
  {
    field: 'address.address_line_1',
    label: 'street address',
    isPresent: p => Boolean(p.address?.addressLine1 || p.address?.address_line_1),
  },
  { field: 'address.city', label: 'city', isPresent: p => Boolean(p.address?.city) },
  { field: 'address.postcode', label: 'postcode', isPresent: p => Boolean(p.address?.postcode) },
  { field: 'telephone', label: 'telephone number', isPresent: p => Boolean(p.telephone) },
];

const REQUIREMENT_LABELS: { [field: string]: string } = REQUIREMENTS.reduce(
  (labels, { field, label }) => ({ ...labels, [field]: label }),
  {}
);

const TOOLTIP = 'Lets patients order repeats from inactive pharmacies. The request goes straight to their GP via IM1.';

const InfoIcon = styled(InfoOutlinedIcon)`
  color: #757575;
  font-size: 20px;
  margin-left: 8px;
  cursor: help;
  vertical-align: middle;
`;

const LabelWithIcon = styled.span`
  display: inline-flex;
  align-items: center;
`;

const MissingRequirements = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #d32f2f;
`;

type InactiveOrderingSettingProps = {
  pharmacy: Pharmacy;
};

const describeMissing = (fields: string[]) => fields.map(field => REQUIREMENT_LABELS[field] || field).join(', ');

export default function InactiveOrderingSetting({ pharmacy }: InactiveOrderingSettingProps) {
  const [setting, setSetting] = useState(pharmacy.inactive_ordering);
  const [saveError, setSaveError] = useState<string[]>([]);
  const { mutate, isLoading } = useUpdateInactiveOrdering(pharmacy._id);

  const missingRequirements = REQUIREMENTS.filter(requirement => !requirement.isPresent(pharmacy)).map(
    ({ field }) => field
  );
  const isOrderable = missingRequirements.length === 0;

  // Unset means on, so only an explicit false turns it off. A branch missing details it needs cannot
  // be ordered from whatever the setting says, so it shows as off too.
  const isEnabled = setting !== false && isOrderable;

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    setSetting(isChecked);
    setSaveError([]);

    mutate(
      { inactive_ordering: isChecked },
      {
        onSuccess: () => {
          enqueueSnackbar(`Inactive ordering turned ${isChecked ? 'on' : 'off'} for this branch`, {
            variant: 'success',
          });
        },
        onError: error => {
          setSetting(pharmacy.inactive_ordering);

          const missing = getMissingRequirements(error);
          setSaveError(missing);

          if (!missing.length) {
            enqueueSnackbar('Could not change inactive ordering, please try again', { variant: 'error' });
          }
        },
      }
    );
  };

  const blockers = saveError.length ? saveError : missingRequirements;

  return (
    <>
      <Switch
        label={
          <LabelWithIcon>
            Inactive ordering
            <Tooltip title={TOOLTIP} placement="right">
              <InfoIcon />
            </Tooltip>
          </LabelWithIcon>
        }
        value={isEnabled}
        onChange={handleToggle}
        checked={isEnabled}
        disabled={isLoading || !isOrderable}
        reverseOrder={false}
      />
      {blockers.length > 0 && (
        <MissingRequirements>
          {`Not orderable until this branch has a ${describeMissing(blockers)}`}
        </MissingRequirements>
      )}
    </>
  );
}
